import { createContext, use, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';

import { LOGIN_MUTATION, LOGOUT_MUTATION, ME_QUERY, REGISTER_MUTATION } from '@/api/documents';
import { setSessionLostHandler } from '@/app/apollo';
import type { MeQuery, PlanTier, UserRole } from '@/graphql/graphql';
import { isPublicSessionPath } from './publicPaths';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  restaurantId?: string | null;
}

export type AuthRestaurant = NonNullable<MeQuery['myRestaurant']>;

interface AuthContextValue {
  user: AuthUser | null;
  restaurant: AuthRestaurant | null;
  tier: PlanTier | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    restaurantName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(ME_QUERY, {
    // The session lives in an httpOnly cookie, so the only way to learn whether
    // the user is signed in is to ask the server on every load.
    fetchPolicy: 'cache-and-network',
  });

  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [registerMutation] = useMutation(REGISTER_MUTATION);
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  // A refresh that fails means the refresh cookie is gone or was replayed;
  // clearing the cache drops any data the previous user could still see, then
  // bounce to login (except public guest routes and the auth screens).
  useEffect(() => {
    setSessionLostHandler(() => {
      void client.clearStore();
      const path = window.location.pathname;
      if (isPublicSessionPath(path)) {
        return;
      }
      const from = `${path}${window.location.search}`;
      window.location.assign(`/login?from=${encodeURIComponent(from)}`);
    });
  }, [client]);

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMutation({ variables: { input: { email, password } } });
      await client.resetStore();
      const result = await client.query({ query: ME_QUERY, fetchPolicy: 'network-only' });
      const me = result.data?.me as AuthUser | null | undefined;
      if (!me) throw new Error('Signed in, but the session could not be loaded');
      return me;
    },
    [client, loginMutation],
  );

  const register = useCallback(
    async (input: { email: string; password: string; name: string; restaurantName: string }) => {
      await registerMutation({ variables: { input } });
      await client.resetStore();
    },
    [client, registerMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation().catch(() => undefined);
    await client.clearStore();
    await refetch();
  }, [client, logoutMutation, refetch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: (data?.me as AuthUser | undefined) ?? null,
      restaurant: data?.myRestaurant ?? null,
      tier: data?.myRestaurant?.tier ?? null,
      // `cache-and-network` reports loading on every background refetch; only
      // the very first pass should block the app behind a spinner.
      loading: loading && data === undefined,
      login,
      register,
      logout,
      refresh: async () => {
        await refetch();
      },
    }),
    [data, loading, login, register, logout, refetch],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
